import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { FormPart } from '@modules/shared/components/FormPart';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { statesList } from '@shared/constants/statesList';
import { NumberInputField } from '@shared/fields/NumberInputField';
import { PhoneInputField } from '@shared/fields/PhoneInputField';
import { SelectDateField } from '@shared/fields/SelectDateField';
import { TextInputField } from '@shared/fields/TextInputField';
import { ZipCodeInputField } from '@shared/fields/ZipCodeInputField';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { colorTheme } from '@styles/muiTheme';
import { useQueryState } from '@shared/hooks/useQueryState';
import { sortArray } from '@shared/utils/helpers';
import { IFormAsset, ILienHolder } from '../interfaces';
import { NContact } from '../../Contact/validation';

const prefix = 'assetLienHolder';

interface ILienForProps {
  lienHolders?: ILienHolder[];
}

interface IAcceleratedPartnerInvokeInfo {
  acceleratedPartnerCode?: string;
  isPayoffAutoInvoke: boolean;
}

export const LienForm = ({ lienHolders = [] }: ILienForProps) => {
  const [assetIdParam] = useQueryState('assetId');
  const { setFieldValue, values } = useFormikContext<IFormAsset>();

  const [selectedPartnerCode, setSelectedPartnerCode] = useState<IAcceleratedPartnerInvokeInfo>();
  const [customMessage, setCustomMessage] = useState('');

  const currentSavedPartnerCode = useMemo(
    () => lienHolders?.find((lh) => lh.id == values.assetLienHolder?.lienHolderId)?.acceleratedTitlePartnerCode,
    [lienHolders, values.assetLienHolder?.lienHolderId]
  );

  useEffect(() => {
    setCustomMessage(values.assetLienHolder?.isAcceleratedTitleSupported ?? false ? 'Accelerated Title' : '');
  }, [values.assetLienHolder?.isAcceleratedTitleSupported ?? false]);

  useEffect(() => {
    setSelectedPartnerCode({ acceleratedPartnerCode: currentSavedPartnerCode, isPayoffAutoInvoke: false });
  }, [currentSavedPartnerCode]);

  const pullPayOffInfo = useCallback(
    async (partnerCode?: string) => {
      Notistack.generalError('Please select Phoenix Lienholder as DealerTrack not supported for new Asset');
      setFieldValue(`${prefix}.address`, {});
      setFieldValue(`${prefix}.address.zip`, '');
      setFieldValue(`${prefix}.showBanner`, false);
      setFieldValue(`${prefix}.phoneNumber`, '');
      setFieldValue(`${prefix}.name`, '');
      setFieldValue(`${prefix}.lienHolderId`, null);
      setSelectedPartnerCode({ acceleratedPartnerCode: partnerCode, isPayoffAutoInvoke: true });
      return;
    },
    [selectedPartnerCode, assetIdParam]
  );

  const handleLienHolderChange = useCallback(async (selectedValue?: string) => {
    if (selectedValue) {
      const selectedLienHolder = lienHolders.find((lienHolder) => lienHolder.id === selectedValue);

      setFieldValue(`${prefix}.address`, { ...(selectedLienHolder?.address || '') });
      setFieldValue(`${prefix}.showBanner`, selectedLienHolder?.showBanner || false);
      setFieldValue(`${prefix}.phoneNumber`, selectedLienHolder?.phoneNumber || '');

      if (selectedLienHolder) {
        setCustomMessage(selectedLienHolder?.acceleratedTitlePartnerCode? 'Accelerated Title' : '' );
        if (selectedLienHolder?.name && selectedLienHolder?.acceleratedTitlePartnerCode) {
          setFieldValue(`${prefix}.name`, selectedLienHolder?.name || '');
          const acceleratedTitlePartnerCode = selectedLienHolder?.acceleratedTitlePartnerCode;
          setSelectedPartnerCode({ acceleratedPartnerCode: acceleratedTitlePartnerCode, isPayoffAutoInvoke: true });
        } else if (selectedLienHolder?.name) {
          setFieldValue(`${prefix}.name`, selectedLienHolder?.name || '');
          setSelectedPartnerCode({ acceleratedPartnerCode: undefined, isPayoffAutoInvoke: true });
        } else {
          const acceleratedTitlePartnerCode = selectedLienHolder?.acceleratedTitlePartnerCode;
          setFieldValue(`${prefix}.name`, selectedLienHolder?.acceleratedTitlePartnerName || '');
          setSelectedPartnerCode({ acceleratedPartnerCode: acceleratedTitlePartnerCode, isPayoffAutoInvoke: true });
        }
        setFieldValue(`${prefix}.lienHolderId`, selectedLienHolder?.id || '');
      } else {
        const partnerDetails = lienHolders.find((lienHolder) => lienHolder.acceleratedTitlePartnerCode === selectedValue);
        const acceleratedTitlePartnerCode = partnerDetails?.acceleratedTitlePartnerCode;
        if (partnerDetails?.acceleratedTitlePartnerName) {
          setFieldValue(`${prefix}.name`, partnerDetails?.acceleratedTitlePartnerName || '');
        } else {
          setFieldValue(`${prefix}.name`, selectedValue || '');
        }
        setSelectedPartnerCode({ acceleratedPartnerCode: acceleratedTitlePartnerCode, isPayoffAutoInvoke: true });
      }
    } else {
      setFieldValue(`${prefix}.address`, {});
      setFieldValue(`${prefix}.address.zip`, '');
      setFieldValue(`${prefix}.showBanner`, false);
      setFieldValue(`${prefix}.phoneNumber`, '');
      setFieldValue(`${prefix}.name`, '');
      setFieldValue(`${prefix}.lienHolderId`, null);
      setSelectedPartnerCode({ acceleratedPartnerCode: undefined, isPayoffAutoInvoke: true });
    }
  }, []);

  useEffect(() => {
    if (selectedPartnerCode?.acceleratedPartnerCode && selectedPartnerCode?.isPayoffAutoInvoke) {
      pullPayOffInfo(selectedPartnerCode.acceleratedPartnerCode);
    }
  }, [selectedPartnerCode]);

  const formatLienholderOptions = useMemo(() => {
    const lienHolderData = values.assetLienHolder;

    const newOption: ILienHolder = {
      acceleratedTitlePartnerCode: '',
      acceleratedTitlePartnerName: '',
      acumaticaVendorId: '',
      address: lienHolderData?.address as unknown as NContact.Address2,
      description: '',
      id: lienHolderData?.lienHolderId || '',
      name: lienHolderData?.name || '',
      numericId: 0,
      phoneNumber: lienHolderData?.phoneNumber || '',
      showBanner: false,
    };

    const isOptionExist = (lienHolders || []).find((lienHolders: ILienHolder) => lienHolders.id === lienHolderData?.lienHolderId);
    const validOptions = isOptionExist ? lienHolders : [...lienHolders, newOption];
    const sortedLienHolder = sortArray(validOptions, 'name', 'asc', true);
    const formattedLienHolders = sortedLienHolder.map((lienHolder: ILienHolder) => ({
      value: lienHolder.id == '' ? lienHolder.acceleratedTitlePartnerCode : lienHolder.id,
      label: lienHolder.name ?? lienHolder.acceleratedTitlePartnerName,
      groupLabel: lienHolder.acceleratedTitlePartnerCode ? 'Dealertrack' : 'Phoenix',
    }));

    return formattedLienHolders;
  }, [lienHolders, values.assetLienHolder]);

  const formConfig = useMemo(
    () => [
      {
        width: '100%',
        name: `${prefix}.loan.loanAmount`,
        label: 'Amount',
        decimalScale: null,
        prefix: '$',
        component: NumberInputField,
      },
      {
        width: '100%',
        name: `${prefix}.loan.rate`,
        label: 'Rate',
        type: 'number',
        suffix: '%',
        decimalScale: 5,
        component: TextInputField,
      },
      {
        width: '100%',
        name: `${prefix}.loan.monthlyPayment`,
        label: 'Payment',
        decimalScale: null,
        prefix: '$',
        component: NumberInputField,
      },
      {
        width: '100%',
        name: `${prefix}.loan.perDiem`,
        label: 'Per Diem',
        decimalScale: null,
        prefix: '$',
        component: NumberInputField,
      },
      {
        width: '100%',
        name: `${prefix}.goodDate`,
        label: 'Good Date',
        component: SelectDateField,
      },
      {
        width: '100%',
        name: `${prefix}.loan.nextPaymentDate`,
        label: 'Next Payment',
        component: SelectDateField,
      },
      {
        width: '100%',
        name: `${prefix}.lienHolderId`,
        options: formatLienholderOptions,
        label: 'Lien Holder',
        placeholder: 'Select Lien Holder',
        customMessage: {
          message: customMessage,
          color: colorTheme.palette.success.dark,
          textTransform: 'uppercase',
          textAlign: 'center',
        },
        onChange: handleLienHolderChange,
        component: SelectAutocompleteField,
      },
      {
        width: '100%',
        name: `${prefix}.accountNumber`,
        label: 'Account Number',
        component: TextInputField,
      },
      {
        width: '100%',
        name: `${prefix}.phoneNumber`,
        label: 'Phone #',
        isAbleToCall: true,
        disabled: true,
        component: PhoneInputField,
      },
      {
        width: '100%',
        name: `${prefix}.address.streetLine1`,
        label: 'Address',
        disabled: true,
        component: TextInputField,
      },
      {
        width: '100%',
        name: `${prefix}.address.streetLine2`,
        label: 'Address 2',
        disabled: true,
        component: TextInputField,
      },
      {
        width: '100%',
        name: `${prefix}.address.city`,
        label: 'City',
        disabled: true,
        component: TextInputField,
      },
      {
        width: '100%',
        name: `${prefix}.address.state`,
        label: 'State',
        options: statesList,
        disabled: true,
        component: TextInputField,
      },
      {
        width: '100%',
        name: `${prefix}.address.zip`,
        label: 'Zip',
        disabled: true,
        component: ZipCodeInputField,
      },
    ],
    [customMessage, formatLienholderOptions, lienHolders]
  );

  return (
    <>     
      <FormPart
        name="Lien"
        config={formConfig}
        marginTop={1}
        marginBottom={2}
      />
    </>
  );
};
